<template>
    <div
        class="doctorMenu"
        @click="openModalReservationDoctorMenuDetail">
        <div class="doctorMenuDoctorWrapper">
            <template v-if="isCampaign">
                <div class="doctorMenuTitle">YOKUMIRU&nbsp;健康応援ウィーク&nbsp;2024</div>
                <p>{{ $locale.consultation_host }}のご予約はこちらから</p>
            </template>
            <template v-else>
                <div class="doctorMenuTitle">ご希望の日時から医師をマッチング</div>
                <p>選択した日程・時間帯に合わせて、あなたのお悩みに<br>最適な医師を{{ $locale.appNameJa }}がご提案します</p>
            </template>

            <div class="doctorMenuRowWrapper">
                <div class="doctorMenuConsultationOverview">
                    <div class="doctorMenuConsultationMinutes">
                        <div>
                            <span>{{ $locale.consultation_host }}時間</span>
                            <span class="large">{{ medicalCourse.matching_minutes }}</span>
                            <span>分</span>
                        </div>
                    </div>
                    <div
                        v-if="isPayPrice"
                        class="doctorMenuConsultationPrice d-flex">
                        <div>
                            <span>{{ $locale.consultation_host }}料</span>
                            <span :class="{ 'discounted' : hasDiscount }" class="large">
                                {{ helper.inTax(discountedPrice).toLocaleString() }}
                            </span>
                            <span :class="{ 'discounted' : hasDiscount }">円</span>
                        </div>
                        <div
                            v-if="hasDiscount"
                            class="doctorMenuConsultationPriceOrigin">
                            <span>{{ helper.inTax(medicalCourse.matching_price).toLocaleString() }}</span>
                            <span>円</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="doctorMenuButton">日時を選択する</div>
    </div>
</template>

<script>
    import constants from '../../constants'
    import YokumiruHelper from '../../YokumiruHelper'
    import moment from 'moment-timezone'
    import { mapMutations } from 'vuex'

    export default {
        props: {
            user: {
                type: Object,
                required: true,
            },
            userCoupons: {
                type: Array,
                default: () => { return [] },
            },
            isCampaign: {
                type: Boolean,
                default: false,
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
            }
        },

        computed: {
            medicalCourse: function () {
                return this.$store.state.reservation.medicalCourse
            },

            /**
             * 相談料の決済が必要かどうか
             * @return {boolean}
             */
            isPayPrice: function () {
                if (this.$locale.type !== 'consul') {
                    return false
                }
                return this.user.plan.is_pay_consultation_price
            },

            /**
             * 最大割引となる所持クーポンを返す
             *
             * @return {object|null}
             */
            maxDiscountCoupon: function () {
                const matchingPrice = this.medicalCourse.matching_price

                // 無料マッチングの場合は割引表示しない
                if (matchingPrice < 1) {
                    return null
                }

                // 相談料の決済が不要なら割引表示しない
                if (!this.isPayPrice) {
                    return null
                }

                let result = null

                this.userCoupons.forEach((userCoupon) => {
                    if (userCoupon.min_consultation_price) {
                        if (matchingPrice < userCoupon.min_consultation_price) {
                            return
                        }
                    }
                    if (userCoupon.has_doctor_menu) {
                        return
                    }
                    if (userCoupon.has_doctor) {
                        return
                    }

                    if (result === null || userCoupon.discount > result.discount) {
                        result = userCoupon
                    }
                })

                return result
            },

            /**
             * 割引されるかどうかを返す
             *
             * @returns {boolean}
             */
            hasDiscount: function () {
                // 相談料の決済が不要なら割引表示しない
                if (!this.isPayPrice) {
                    return false
                }
                return this.maxDiscountCoupon !== null
            },

            /**
             * 割引適用後の相談料金を返す
             *
             * @returns {number}
             */
            discountedPrice: function () {
                const matchingPrice = this.medicalCourse.matching_price

                if (this.maxDiscountCoupon) {
                    const price = matchingPrice - this.maxDiscountCoupon.discount
                    return price < 1 ? 0 : price
                }
                return matchingPrice
            },
        },

        created: function () {
            moment.locale('ja')
        },

        methods: {
            ...mapMutations({
                setMode: 'reservation/setMode',
                setDoctorMenu: 'reservation/setDoctorMenu',
                setAsRepeat: 'reservation/setAsRepeat',
            }),

            /**
             * メニュー詳細モーダル表示
             */
            openModalReservationDoctorMenuDetail: function () {
                const prevDoctorMenu = this.$store.state.reservation.doctorMenu

                // 医師指定予約→マッチング予約に変えた場合は問診内容をクリア
                if (prevDoctorMenu) {
                    bus.$emit('clear-reservation-interview-questions')
                }

                this.pushAnalyticsDataLayerSelectDoctorMenu()
                this.setMode('matching')
                this.setAsRepeat(false)
                this.setDoctorMenu(null)

                bus.$emit('open-modal-reservation-doctor-menu-detail')
            },

            /**
             * GoogleAnalytics GA4 カスタムイベント発火
             */
            pushAnalyticsDataLayerSelectDoctorMenu: function () {
                if (this.$locale.type !== 'consul') return

                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'dl__selectDoctorMenu',
                    doctorMenuId: null,
                    doctorMenuTitle: '日付から予約',
                })
            },
        },
    }
</script>

<style lang="scss" scoped>
.doctorMenu {
    position: relative;
    display: block;
    vertical-align: top;
    width: 100%;
    margin: 15px auto 25px;
    padding: 18px 20px 15px 20px;
    background-color: #FFF2F2;
    border: 3px solid #F892AA;
    border-radius: 6px;
    cursor: pointer;
    transition: opacity 0.1s ease-in-out;
    background-image: url("/images/common/banner__recommend-img.png");
    background-position: 98% bottom;
    background-repeat: no-repeat;
    background-size: 184px;

    &:hover {
        opacity: 0.7;
    }

    @media screen and (max-width: 991px) {
        background-size: 150px;
    }

    @media screen and (max-width: 560px) {
        background-size: 130px;
    }
}

.doctorMenuDoctorWrapper {
    width: 450px;

    @media screen and (max-width: 991px) {
        max-width: 400px;

        p {
            font-size: 13px;
            text-shadow: 0 0 2px rgba(255, 255, 255, 1);
        }
    }
}

.doctorMenuTitle {
    margin-bottom: 4px;
    color: #2A2E31;
    font-size: 24px;
    font-weight: bold;
    line-height: 1.35;

    @media screen and (max-width: 991px) {
        font-size: 20px;
    }
}

.doctorMenuConsultationOverview {
    color: #7D868D;
    line-height: 1.1;
    font-size: 18px;
    font-weight: bold;

    @media screen and (max-width: 991px) {
        font-size: 14px;
    }

    .large {
        font-size: 26px;

        @media screen and (max-width: 991px) {
            font-size: 22px;
        }
    }

    .discounted {
        color: #CE798A;
    }

    .doctorMenuConsultationMinutes {
        @media screen and (max-width: 991px) {
            font-size: 13px;
        }
    }

    .doctorMenuConsultationPrice {
        @media screen and (max-width: 991px) {
            font-size: 13px;
        }

        .doctorMenuConsultationPriceOrigin {
            margin: 7px 0 0 5px;
            text-decoration: line-through;
        }
    }
}

.doctorMenuLabel {
    display: inline-block;
    margin-right: 5px;
    margin-bottom: 5px;
    padding: 2px 12px;
    color: #53A15B;
    font-size: 13px;
    border: 1px solid #DFEDDC;
    background-color: #F3F8F2;
    border-radius: 6px;

    &:last-child {
        margin-right: 0;
    }
}

.divide {
    border-top: 1px dotted #ABB3BA;
}

.doctorMenuDoctorImageWrapper {
    position: relative;

    .doctorMenuDoctorImage {
        position: relative;
        overflow: hidden;
        width: 130px;
        min-width: 130px;
        height: 130px;
        border-radius: 50%;

        img {
            object-fit: cover;
            width: 100%;
            height: 100%;
        }
    }

    .doctorMenuDoctorDetailIcon {
        position: absolute;
        right: 4px;
        bottom: 4px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 4px solid #F9F9F9;
        background: #487EAF;

        @media screen and (max-width: 767px) {
            right: auto;
            left: 102px;
        }

        svg {
            color: #fff;
            font-size: 16px;
        }
    }
}

.doctorMenuDoctorName {
    line-height: 1.2;
    color: #2A2E31;
    font-size: 18px;
    font-weight: bold;
}

.doctorMenuDoctorDetail {
    margin: 6px 0 0 16px;

    @media screen and (max-width: 767px) {
        margin: 10px 0 0 10px;
    }
}

.doctorMenuDoctorMedicalCourse {
    display: inline-block;
    color: #7D868D;
    font-size: 14px;
    font-feature-settings: 'palt';
    line-height: 1.5;
}

.doctorMenuDoctorLabel {
    display: inline-block;
    margin-right: 5px;
    margin-bottom: 5px;
    padding: 2px 12px;
    color: #B49834;
    font-size: 13px;
    border: 1px solid #EDECDC;
    background-color: #F7F8F2;
    border-radius: 6px;

    &:last-child {
        margin-right: 0;
    }
}

.doctorMenuRowWrapper {
    width: 350px;
    box-sizing: border-box;
    padding: 14px 18px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 3px 3px 0 #eedddd;

    @media screen and (max-width: 991px) {
        width: 35vw;
        padding: 12px;
    }

    @media screen and (max-width: 560px) {
        width: 55vw;
    }
}

.doctorMenuButton {
    width: 54%;
    padding: 7px;
    margin: 15px auto 0 auto;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    text-align: center;
    background-color: #F892AA;
    background-image: url("/images/common/btn_matching_arrow.png");
    background-position: 95% center;
    background-repeat: no-repeat;
    background-size: 20px;
    border-radius: 30px;

    @media screen and (max-width: 991px) {
        width: 64%;
        padding: 6px;
        font-size: 14px;
        background-size: 16px;
    }
}
</style>
